import { useTranslation } from 'react-i18next';
import { formatWithThousandSeparator } from '@/utils';
import type { MerchantPlanRate } from '@/types';

/**
 * The plan's per-destination prices, one row per scope. Rendered for every
 * pricing model (tier and custom) — one headline number was a lie for every
 * plan since the July book split Baghdad from the provinces.
 *
 * The catch-all (province_code: null) reads "Other provinces" only when a
 * scoped row exists above it; alone, it is simply the account's flat rate
 * and saying "other" would imply a distinction that isn't there.
 */
interface PlanRatesProps {
  rates: MerchantPlanRate[];
}

const PROVINCE_LABEL_KEY: Record<string, string> = {
  BGD: 'pricing_rate_baghdad',
  BAS: 'pricing_rate_basra',
};

const PlanRates = ({ rates }: PlanRatesProps) => {
  const { t } = useTranslation();
  const hasScopedRow = rates.some((rate) => rate.province_code !== null);

  const label = (rate: MerchantPlanRate) => {
    if (rate.province_code === null) {
      return hasScopedRow ? t('pricing_rate_other_provinces') : t('pricing_rate_all_provinces');
    }
    return t(PROVINCE_LABEL_KEY[rate.province_code] ?? '', {
      defaultValue: rate.province_code,
    });
  };

  return (
    <div className='divide-y divide-sd-border rounded-xl border border-sd-border'>
      {rates.map((rate) => (
        <div
          key={rate.province_code ?? 'catch-all'}
          className='flex items-center justify-between px-4 py-3'
        >
          <span className='text-sm text-sd-foreground'>{String(label(rate))}</span>
          <span className='text-sm font-semibold text-sd-foreground'>
            <bdi dir='ltr' className='tabular-nums'>
              {formatWithThousandSeparator(rate.price_iqd)} {String(t('iqd'))}
            </bdi>{' '}
            <span className='font-normal text-sd-muted-foreground'>
              {String(t('pricing_rate_per_order'))}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
};

export default PlanRates;
